// WordPress dependencies
// eslint-disable-next-line @wordpress/no-unsafe-wp-apis
import {
    __experimentalBoxControl as BoxControl,
    __experimentalUnitControl as UnitControl,
    Button,
    ColorIndicator,
    PanelBody,
    PanelRow,
    RangeControl,
    SelectControl,
} from "@wordpress/components";
import { __ } from "@wordpress/i18n";
import { useState } from "@wordpress/element";

// Internal dependencies
import ProFeatureControl from "../elements/pro-feature-control";
const coreAssets = window.COCA_BAIS_ASSETS;
import { getNumberFromString, ucFirst } from "../../utils";
import ColorControl from "../control/color-control";
import GradientControl from "../control/gradient-control";

export default (props) => {
    const [toggleOptions, updateToggleOptions] = useState({});
    const isTripleEnabled = !!props.proSettings.is_triple_active;
    const sliderType = props.attributes.slider_type;
    const isTriple = isTripleEnabled && sliderType === "triple";

    const getAttr = (attribute) => {
        const store = props.attributes;
        // For numeric values like label_alignment, return 0 instead of empty string when not found
        if (attribute === "label_alignment") {
            return store[attribute] !== undefined ? store[attribute] : 0;
        }
        return !!store[attribute] ? store[attribute] : "";
    };

    const verticalAlignments = [
        { label: __("Left", "wp-before-after-image-slider"), value: "left" },
        { label: __("Center", "wp-before-after-image-slider"), value: "middle" },
        { label: __("Right", "wp-before-after-image-slider"), value: "right" },
    ];
    const horizontalAlignments = [
        { label: __("Top", "wp-before-after-image-slider"), value: "top" },
        { label: __("Center", "wp-before-after-image-slider"), value: "middle" },
        { label: __("Bottom", "wp-before-after-image-slider"), value: "bottom" },
    ];

    const LabelAlignmentControls = () => (
        <div className={"field"}>
            {props.attributes.orientation === "horizontal" && (
                <SelectControl
                    className="large-select"
                    onChange={(value) => props.updateAttributes({ label_horizontal_alignment: value })}
                    value={getAttr("label_horizontal_alignment")}
                    options={horizontalAlignments}
                />
            )}
            {props.attributes.orientation === "vertical" && (
                <SelectControl
                    className="large-select"
                    onChange={(value) => props.updateAttributes({ label_vertical_alignment: value })}
                    value={getAttr("label_vertical_alignment")}
                    options={verticalAlignments}
                />
            )}
        </div>
    );

    const LabelAlign = (
        <div className={"field-control field-inline"}>
            <span className="field-control__label">{__("Align", "wp-before-after-image-slider")}</span>
            <div className={"fields-wrapper inline"}>
                <div
                    className={"group-field"}
                    style={{ width: "100%", display: "flex", gap: "10px", marginRight: "12px" }}
                >
                    <RangeControl
                        onChange={(value) => props.updateAttributes({ label_alignment: value })}
                        initialPosition={10}
                        max={100}
                        min={0}
                        step={1}
                        allowReset={true}
                        withInputField={false}
                        trackColor="#4A25E1"
                        railColor="#603CFF7A"
                        value={parseInt(props.attributes.label_alignment) || 10}
                    />
                </div>
            </div>
        </div>
    );

    const LabelTextColorsField = (
        <>
            <div className={"field"}>
                {ColorControl({
                    props,
                    attribute: "label_text_color_before",
                    labelText: __("Before", "wp-before-after-image-slider"),
                    resetText: __("Clear", "wp-before-after-image-slider"),
                })}
            </div>
            <div className={"field"}>
                {isTriple &&
                    ColorControl({
                        props,
                        attribute: "label_text_color_middle",
                        labelText: __("Middle", "wp-before-after-image-slider"),
                        resetText: __("Clear", "wp-before-after-image-slider"),
                    })}
            </div>
            <div className={"field"}>
                {ColorControl({
                    props,
                    attribute: "label_text_color_after",
                    labelText: __("After", "wp-before-after-image-slider"),
                    resetText: __("Clear", "wp-before-after-image-slider"),
                })}
            </div>
        </>
    );
    const LabelBackgroundColorsField = (
        <>
            <div className={"field"}>
                {ColorControl({
                    props,
                    attribute: "label_background_color_before",
                    labelText: __("Before", "wp-before-after-image-slider"),
                    resetText: __("Clear", "wp-before-after-image-slider"),
                })}
            </div>
            <div className={"field"}>
                {isTriple &&
                    ColorControl({
                        props,
                        attribute: "label_background_color_middle",
                        labelText: __("Middle", "wp-before-after-image-slider"),
                        resetText: __("Clear", "wp-before-after-image-slider"),
                    })}
            </div>
            <div className={"field"}>
                {ColorControl({
                    props,
                    attribute: "label_background_color_after",
                    labelText: __("After", "wp-before-after-image-slider"),
                    resetText: __("Clear", "wp-before-after-image-slider"),
                })}
            </div>
        </>
    );

    const BorderTypeField = (
        <SelectControl
            className="large-select"
            value={props.attributes.label_border_style}
            onChange={(value) =>
                props.updateAttributes({
                    label_border_style: value,
                })
            }
            options={[].map.call(["none", "solid", "double", "dotted", "dashed", "groove"], (borderType) => ({
                // eslint-disable-next-line @wordpress/i18n-no-variables
                label: __(ucFirst(borderType), "wp-before-after-image-slider"),
                value: borderType,
            }))}
        />
    );

    return (
        <PanelBody
            title={
                <div className="components-panel__body-title__content">
                    <span>{__("Label", "wp-before-after-image-slider")} </span>
                    {!!props.proFeatureEnabled ? null : (
                        <a
                            href="https://codecanel.com/wp-before-after-image-slider/#product-pricing"
                            target="_blank"
                            style={{ marginTop: "6px" }}
                            title="Buy Pro"
                            onClick={function (e) {
                                e.stopPropagation();
                            }}
                        >
                            <img src={coreAssets.icons.premium} alt="Premium Icon" />
                        </a>
                    )}
                </div>
            }
            initialOpen={false}
            opened={props.attributes.label_panel_open}
            onToggle={() => props.updateAttributes({ label_panel_open: !props.attributes.label_panel_open })}
        >
            <PanelRow>
                <ProFeatureControl {...props} value={"label_horizontal_alignment"}>
                    <div className={"field-control field-inline"}>
                        <span className="field-control__label">{__("Alignment", "wp-before-after-image-slider")}</span>
                        <div className={"fields-wrapper inline"}>{LabelAlignmentControls()}</div>
                    </div>

                    {LabelAlign}

                    <div className={"field-control field-inline"}>
                        <span className="field-control__label">{__("Text Color", "wp-before-after-image-slider")}</span>
                        <div className={"fields-wrapper inline"}>{LabelTextColorsField}</div>
                    </div>
                    <div className={"field-control field-inline"}>
                        <span className="field-control__label">
                            {__("Background Color", "wp-before-after-image-slider")}
                        </span>
                        <div className={"fields-wrapper inline"}>{LabelBackgroundColorsField}</div>
                    </div>
                    <div className={"field-control field-inline"}>
                        <span className="field-control__label">
                            {__("Background Gradient", "wp-before-after-image-slider")}
                        </span>
                        <div className={"fields-wrapper inline"}>
                            <div className={"field"}>
                                {GradientControl({
                                    props,
                                    attribute: "label_background_gradient_before",
                                    defaultValue: getAttr("label_background_gradient_before"),
                                    labelText: __("Before", "wp-before-after-image-slider"),
                                })}
                            </div>
                            {isTriple && (
                                <div className={"field"}>
                                    {GradientControl({
                                        props,
                                        attribute: "label_background_gradient_middle",
                                        defaultValue: getAttr("label_background_gradient_middle"),
                                        labelText: __("Middle", "wp-before-after-image-slider"),
                                    })}
                                </div>
                            )}
                            <div className={"field"}>
                                {GradientControl({
                                    props,
                                    attribute: "label_background_gradient_after",
                                    defaultValue: getAttr("label_background_gradient_after"),
                                    labelText: __("After", "wp-before-after-image-slider"),
                                })}
                            </div>
                        </div>
                    </div>

                    <div className={"field-control field-inline"}>
                        <span className="field-control__label">
                            {__("Border Style", "wp-before-after-image-slider")}
                        </span>
                        <div className={"fields-wrapper inline"}>{BorderTypeField}</div>
                    </div>
                    {props.attributes.label_border_style !== "none" && (
                        <>
                            <div className={"field-control field-inline"}>
                                <span className="field-control__label">
                                    {__("Border Width:", "wp-before-after-image-slider")}
                                </span>
                                <div className={"fields-wrapper inline"}>
                                    <div
                                        className={"group-field"}
                                        style={{ width: "100%", display: "flex", gap: "10px", marginRight: "12px" }}
                                    >
                                        <UnitControl
                                            className="coca-unit-control"
                                            isPressEnterToChange
                                            onChange={(value) => props.updateAttributes({ label_border_width: value })}
                                            value={getAttr("label_border_width")}
                                        />
                                        <RangeControl
                                            onChange={(value) =>
                                                props.updateAttributes({
                                                    label_border_width: value,
                                                })
                                            }
                                            max={100}
                                            min={0}
                                            step={1}
                                            allowReset={true}
                                            withInputField={false}
                                            value={getNumberFromString(getAttr("label_border_width"))}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className={"field-control field-inline"}>
                                <span className="field-control__label">
                                    {__("Border Color:", "wp-before-after-image-slider")}
                                </span>
                                <div className={"fields-wrapper inline"}>
                                    <div className={"field"}>
                                        {ColorControl({
                                            props,
                                            attribute: "label_border_color_normal",
                                            labelText: __("Default", "wp-before-after-image-slider"),
                                            resetText: __("Clear", "wp-before-after-image-slider"),
                                        })}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    <div className={"field-control field-inline"}>
                        <span className="field-control__label">{__("Padding", "wp-before-after-image-slider")}</span>
                        <div className={"fields-wrapper inline"}>
                            <div className={"group-field"}>
                                <BoxControl
                                    className="coca-box-control"
                                    label={""}
                                    values={getAttr("label_padding")}
                                    onChange={(value) => props.updateAttributes({ label_padding: value })}
                                />
                            </div>
                        </div>
                    </div>
                </ProFeatureControl>
            </PanelRow>
        </PanelBody>
    );
};
